import { Form } from '@form/Form';
import FormInput from '@form/FormInput';
import { useApi } from '@hooks/useApi';
import Joi from 'joi';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MemberFile } from './types';

interface Props {
    memberId: number;
    file: MemberFile;
    onSaved: (file: MemberFile) => void;
}

export const ClubMemberFileEditForm = ({ memberId, file, onSaved }: Props) => {
    const { t } = useTranslation('member-management');
    const api = useApi();
    const [isLoading, setIsLoading] = useState(false);
    // members always see their own uploads, the backend ignores the switch for them
    const isMemberUpload = file.source === 'member';

    const defaultValues = useMemo(
        () => ({
            title: file.title ?? file.display_title,
            valid_until: file.valid_until ? moment(file.valid_until).toDate() : null,
            visible_to_member: !!file.visible_to_member
        }),
        [file]
    );

    const handleSubmit = (values: any) => {
        setIsLoading(true);
        return api({
            url: `club-member/${memberId}/files/${file.id}`,
            method: 'PATCH',
            data: {
                title: values.title || null,
                valid_until: values.valid_until ? moment(values.valid_until).format('YYYY-MM-DD') : null,
                ...(isMemberUpload ? {} : { visible_to_member: !!values.visible_to_member })
            },
            onFinish: () => setIsLoading(false),
            onSuccess: ({ data }) => onSaved(data)
        });
    };

    return (
        <Form
            onSubmit={handleSubmit}
            defaultValues={defaultValues}
            validationSchema={Joi.object({
                title: Joi.string().allow('', null).max(191).label(t('clubMember.files.form.title')),
                valid_until: Joi.date().allow(null).label(t('clubMember.files.form.validUntil')),
                visible_to_member: Joi.boolean()
            })}
        >
            <FormInput name="title" label={t('clubMember.files.form.title')} helperText={file.name} />
            <FormInput.DatePicker
                name="valid_until"
                label={t('clubMember.files.form.validUntil')}
                helperText={t('clubMember.files.form.validUntilHelp')}
            />
            {!isMemberUpload && (
                <FormInput.Checkbox
                    name="visible_to_member"
                    label={
                        <span className="tw-flex tw-flex-col">
                            <span>{t('clubMember.files.form.visibleToMember')}</span>
                            <span className="tw-text-xs tw-text-accents-6">
                                {t('clubMember.files.form.visibleToMemberHelp')}
                            </span>
                        </span>
                    }
                />
            )}
            <FormInput.Submit isLoading={isLoading}>{t('clubMember.files.form.save')}</FormInput.Submit>
        </Form>
    );
};

export default ClubMemberFileEditForm;
